import { Component, type ErrorInfo, type ReactNode } from 'react';
import { type WithTranslation, withTranslation } from 'react-i18next';
import { GlobalError } from '@/components/common/error/GlobalError';
import { getEnv } from '@/lib/utils/common/getEnv';

interface GlobalErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

interface GlobalErrorBoundaryProps extends WithTranslation {
  children: ReactNode;
  fallback?: ReactNode;
}

export const logoutViaAuth0 = () => {
  const { auth0Domain, auth0ClientId } = getEnv();
  const returnTo = encodeURIComponent(window.location.origin);
  window.location.href = `https://${auth0Domain}/v2/logout?returnTo=${returnTo}&client_id=${auth0ClientId}`;
};

class GlobalErrorBoundaryClass extends Component<
  GlobalErrorBoundaryProps,
  GlobalErrorBoundaryState
> {
  constructor(props: GlobalErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): GlobalErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(_error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return <GlobalError error={this.state.error} errorInfo={this.state.errorInfo} />;
    }

    return this.props.children;
  }
}

export const GlobalErrorBoundary = withTranslation()(GlobalErrorBoundaryClass);
